// Los tres mundos de MiPAE en una sola app (decisión del fundador 2026-10-04,
// plan en docs/plan-tres-mundos.md): MiPAE Estudiante, MiPAE Enfermero y
// MiPAE TENS. Cada persona tiene una CASA, la de su rol (lib/roles.js), y ahí
// vale lo que paga. Desde Mi perfil puede pasar a otro mundo "como los
// perfiles de Chrome": cambia lo que se VE (inicio, menú, firma del header,
// chips de Benito), no quién es. Al abrir la app siempre vuelve a su casa
// (M5), por eso el mundo vive solo en memoria de React, jamás en localStorage
// ni en la cuenta.
//
// Los nombres van con mayúscula (decisión del fundador 2026-10-16, "MiPAE
// Estudiante"): es el nombre de la experiencia, igual que en /precios y en
// los Términos. Un rename aplica en todas las superficies a la vez.
//
// Dominio puro: qué mundos hay, a cuáles puede pasar cada casa y qué perfil
// usa la pantalla en el mundo visitado. El perfil REAL sigue mandando en
// datos, guardado, cuotas, plan, telemetría de identidad y en la frontera del
// TENS (lib/tens.js), que no se cruza por cambiar de mundo.
import { ROLES_VALIDOS } from './roles';

// `descripcion` es lo que se ve bajo "Pasar a …" en Mi perfil: qué trae ese
// mundo, en pocas palabras.
export const MUNDOS = Object.freeze([
  { id: 'estudiante', nombre: 'MiPAE Estudiante', descripcion: 'El PAE, los modelos y el estudio' },
  { id: 'enfermero',  nombre: 'MiPAE Enfermero',  descripcion: 'El turno y sus registros' },
  { id: 'tens',       nombre: 'MiPAE TENS',       descripcion: 'El registro de cuidados del TENS' },
]);

const NOMBRE = Object.freeze(Object.fromEntries(MUNDOS.map((m) => [m.id, m.nombre])));

// Quién puede pasar a qué (M1 y M7). TENS → MiPAE Enfermero queda CERRADO
// por ahora: ahí se diagnostica y se escribe el documento del enfermero (la
// evolución por sistemas), que es justo la frontera del DOCUMENTO. No es un
// límite sobre la persona: desde 2026-10-04 el TENS también valora, y lo hace en
// su propio registro. Abrirlo es sumar 'enfermero' a su lista si un caso real lo
// pide.
const PASOS = Object.freeze({
  estudiante: Object.freeze(['enfermero']),
  enfermero: Object.freeze(['estudiante', 'tens']),
  tens: Object.freeze(['estudiante']),
});

// Kill-switch ON por defecto: apagado, Mi perfil no ofrece pasar de mundo y
// todo se comporta como antes (la casa es el único mundo).
export function mundosHabilitados() {
  return process.env.REACT_APP_MUNDOS_ENABLED !== 'false';
}

/** Nombre visible del mundo ("MiPAE Estudiante"), o null fuera del catálogo. */
export function nombreMundo(id) {
  return NOMBRE[id] || null;
}

/** La casa de la persona: su rol, si es uno de los tres. Sin rol no hay casa. */
export function casaDe(profile) {
  const rol = profile?.rol;
  return ROLES_VALIDOS.includes(rol) ? rol : null;
}

/** Los mundos a los que puede pasar desde su casa, en el orden del catálogo. */
export function mundosDisponibles(profile) {
  if (!mundosHabilitados()) return [];
  const casa = casaDe(profile);
  if (!casa) return [];
  const pasos = PASOS[casa] || [];
  return MUNDOS.filter((m) => pasos.includes(m.id));
}

/** ¿Puede estar en ese mundo? Su casa siempre; los demás según la tabla. */
export function puedeEstarEn(profile, mundo) {
  const casa = casaDe(profile);
  if (!casa || !mundo) return false;
  if (mundo === casa) return true;
  return mundosDisponibles(profile).some((m) => m.id === mundo);
}

/**
 * ¿Está fuera de casa? Solo con un mundo válido distinto de la casa: un mundo
 * que su casa no permite (por ejemplo, después de cambiar el rol en Mi perfil)
 * cuenta como estar en casa, nunca como una visita a medias.
 */
export function estaDeVisita(profile, mundo) {
  const casa = casaDe(profile);
  return !!casa && !!mundo && mundo !== casa && puedeEstarEn(profile, mundo);
}

/**
 * El perfil con el que se PINTA el mundo. En casa (o sin mundo, o con un
 * mundo no permitido) devuelve el MISMO objeto, así nada cambia para quien no
 * se mueve. De visita devuelve una copia con el rol del mundo:
 * - en MiPAE Estudiante la etapa es la de un estudiante en práctica (no se
 *   inventa un internado, que traería el turno como superficie primaria);
 * - en los otros mundos no hay etapa;
 * - la jefatura no se pinta: es un permiso sobre el rol de la casa, y la
 *   firma del header debe decir el mundo donde está. El permiso real sigue
 *   en el perfil real, que es el que decide /jefatura.
 * `mundoVisitado` y `casa` viajan en la copia para quien necesite saberlo
 * (la banda, Benito), nunca se guardan: la copia jamás va a la base de datos.
 * @param {object|null} profile
 * @param {string|null} mundo
 * @returns {object|null}
 */
export function perfilParaMundo(profile, mundo) {
  if (!estaDeVisita(profile, mundo)) return profile;
  return {
    ...profile,
    rol: mundo,
    etapa_internado: mundo === 'estudiante' ? 'practica_pregrado' : null,
    ve_jefatura: false,
    mundoVisitado: mundo,
    casa: casaDe(profile),
  };
}

// Una línea para Benito cuando la persona está de visita. Benito sigue
// sabiendo quién es (su contexto de capa sale del perfil real: un enfermero
// que mira MiPAE Estudiante no pasa a ser estudiante), y además sabe qué
// espacio está mirando para no ofrecerle lo de su casa. Texto de contexto
// para el modelo, no visible.
const VISITA_BENITO = Object.freeze({
  estudiante: 'Ahora está mirando MiPAE Estudiante, el espacio para estudiar y practicar el PAE con casos ficticios. Si te pide estudiar o armar un PAE de práctica, ayúdale con casos ficticios, nunca con sus pacientes reales, sin cambiar quién es.',
  enfermero: 'Ahora está mirando MiPAE Enfermero, el espacio del turno profesional. Puede explorarlo, pero no le hables como si ya trabajara turnos.',
  tens: 'Ahora está mirando MiPAE TENS, el espacio del técnico o técnica en enfermería. Puede explorarlo para conocerlo.',
});

/** La línea de visita para el [Contexto] de Benito, o '' en casa. */
export function contextoVisitaBenito(profile, mundo) {
  if (!estaDeVisita(profile, mundo)) return '';
  return VISITA_BENITO[mundo] || '';
}

// Benito arma su contexto al empezar una conversación, pero se puede pasar
// de mundo a mitad de ella (o reabrir la app en casa con un hilo de visita
// aún vigente). Estas dos funciones le avisan el cambio en la vuelta
// siguiente: `mundoSegunConversacion` lee en qué mundo cree Benito que está
// la persona (la última línea de visita o de vuelta que recibió) y
// `contextoCambioMundoBenito` arma el aviso del mundo actual.
// Sin distinguir mayúsculas: un hilo vigente (12 h) puede traer la línea con
// el nombre de antes del 2026-10-16 ("MiPAE estudiante") y tiene que seguir
// leyéndose igual.
const RE_VISITA = /Ahora está mirando (MiPAE (?:estudiante|enfermero|TENS))/i;
const RE_VUELTA = /Ahora volvió a su casa/;
const ID_POR_NOMBRE = Object.freeze(Object.fromEntries(MUNDOS.map((m) => [m.nombre.toLowerCase(), m.id])));

/** El mundo que la conversación le dijo a Benito por última vez (null = casa). */
export function mundoSegunConversacion(conv) {
  const lista = Array.isArray(conv) ? conv : [];
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    const m = lista[i];
    if (!m || m.role !== 'user' || typeof m.content !== 'string') continue;
    const visita = RE_VISITA.exec(m.content);
    const vuelta = RE_VUELTA.test(m.content);
    if (visita) return ID_POR_NOMBRE[visita[1].toLowerCase()] || null;
    if (vuelta) return null;
  }
  return null;
}

/** El aviso de cambio de mundo para Benito, como bloque [Contexto]. */
export function contextoCambioMundoBenito(profile, mundo) {
  const casa = casaDe(profile);
  if (!casa) return '';
  if (estaDeVisita(profile, mundo)) return `[Contexto: ${VISITA_BENITO[mundo]}]`;
  return `[Contexto: Ahora volvió a su casa, ${nombreMundo(casa)}. Háblale como siempre.]`;
}

// Los ramos son de MiPAE Estudiante (definición del 16-oct, ramosDisponibles
// en planGating). Con los mundos apagados (interruptor de emergencia) nadie
// puede pasar a MiPAE Estudiante, así que se leen donde está la persona: el
// mismo criterio que el PAE (paeEnEsteMundo en App.jsx). La membresía sigue
// decidiendo; esto solo evita que queden inalcanzables.
export function mundoParaRamos(mundoEnUso) {
  return mundosHabilitados() ? mundoEnUso : 'estudiante';
}
